import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/config/firebase'
import type { UserStats, GameStats, GameTransaction } from '@/types'

// Default stats
export const getDefaultStats = (): UserStats => ({
  totalWagered: 0,
  totalWon: 0,
  totalLost: 0,
  totalBets: 0,
  totalWins: 0,
  totalLosses: 0,
  ruleta: getDefaultGameStats(),
  blackjack: getDefaultGameStats(),
  baccarat: getDefaultGameStats(),
  slots: getDefaultGameStats(),
})

export const getDefaultGameStats = (): GameStats => ({
  wagered: 0,
  won: 0,
  lost: 0,
  bets: 0,
  wins: 0,
  losses: 0,
})

// Record a game transaction
export async function recordGameTransaction(
  userId: string,
  userEmail: string,
  userName: string,
  game: 'ruleta' | 'blackjack' | 'baccarat' | 'slots',
  betAmount: number,
  winAmount: number,
  result: 'win' | 'loss' | 'push',
  betDetails?: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  try {
    // Calculate net result
    const netResult = winAmount - betAmount

    // Create transaction record
    const transaction: Omit<GameTransaction, 'id'> = {
      userId,
      userEmail,
      userName,
      game,
      type: result === 'win' ? 'win' : result === 'loss' ? 'loss' : 'loss', // push counts as loss for stats
      amount: betAmount,
      netResult,
      betDetails,
      createdAt: new Date(),
    }

    // Add to gameTransactions collection
    await addDoc(collection(db, 'gameTransactions'), {
      ...transaction,
      createdAt: serverTimestamp(),
    })

    // Update user stats
    await updateUserStats(userId, game, betAmount, winAmount, result)

    return { success: true }
  } catch (error) {
    console.error('Error recording game transaction:', error)
    return { success: false, error: 'Error registrando transacción' }
  }
}

// Update user stats after a game
async function updateUserStats(
  userId: string,
  game: 'ruleta' | 'blackjack' | 'baccarat' | 'slots',
  betAmount: number,
  winAmount: number,
  result: 'win' | 'loss' | 'push'
): Promise<void> {
  const userRef = doc(db, 'users', userId)
  const userDoc = await getDoc(userRef)

  if (!userDoc.exists()) return

  const currentData = userDoc.data()
  const currentStats: UserStats = currentData.stats || getDefaultStats()

  // Update total stats
  const isWin = result === 'win'
  const isLoss = result === 'loss'
  const netResult = winAmount - betAmount

  const updatedStats: UserStats = {
    totalWagered: currentStats.totalWagered + betAmount,
    totalWon: currentStats.totalWon + (netResult > 0 ? netResult : 0),
    totalLost: currentStats.totalLost + (netResult < 0 ? Math.abs(netResult) : 0),
    totalBets: currentStats.totalBets + 1,
    totalWins: currentStats.totalWins + (isWin ? 1 : 0),
    totalLosses: currentStats.totalLosses + (isLoss ? 1 : 0),
    ruleta: currentStats.ruleta || getDefaultGameStats(),
    blackjack: currentStats.blackjack || getDefaultGameStats(),
    baccarat: currentStats.baccarat || getDefaultGameStats(),
    slots: currentStats.slots || getDefaultGameStats(),
  }

  // Update game-specific stats
  const gameStats = updatedStats[game] || getDefaultGameStats()
  updatedStats[game] = {
    wagered: gameStats.wagered + betAmount,
    won: gameStats.won + (netResult > 0 ? netResult : 0),
    lost: gameStats.lost + (netResult < 0 ? Math.abs(netResult) : 0),
    bets: gameStats.bets + 1,
    wins: gameStats.wins + (isWin ? 1 : 0),
    losses: gameStats.losses + (isLoss ? 1 : 0),
  }

  await setDoc(userRef, { stats: updatedStats }, { merge: true })
}

// Get user stats
export async function getUserStats(userId: string): Promise<UserStats> {
  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)

    if (userDoc.exists() && userDoc.data().stats) {
      return userDoc.data().stats as UserStats
    }

    return getDefaultStats()
  } catch (error) {
    console.error('Error getting user stats:', error)
    return getDefaultStats()
  }
}

// Get game history for a user
export async function getGameHistory(
  userId: string,
  gameFilter?: 'ruleta' | 'blackjack' | 'baccarat' | 'slots',
  maxResults: number = 50
): Promise<GameTransaction[]> {
  try {
    let q = query(
      collection(db, 'gameTransactions'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    )

    if (gameFilter) {
      q = query(
        collection(db, 'gameTransactions'),
        where('userId', '==', userId),
        where('game', '==', gameFilter),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      )
    }

    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    })) as GameTransaction[]
  } catch (error) {
    console.error('Error getting game history:', error)
    return []
  }
}

// Calculate win rate
export function calculateWinRate(wins: number, totalGames: number): number {
  if (totalGames === 0) return 0
  return Math.round((wins / totalGames) * 100)
}

// Format stats for display
export function formatStatsNumber(num: number): string {
  return new Intl.NumberFormat('es-AR').format(num)
}

export function formatStatsCurrency(num: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
  }).format(num)
}
